/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commerical BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-10-16 06:27:49
 *
 *******************************************************/
// @flow

import _ from 'lodash';

import type { SynonymsState, SynonymsAction } from 'stores/flow-type/synonym.type';

import {
  GET_SYNONYMS_REQUEST,
  GET_SYNONYMS_SUCCESS,
  GET_SYNONYMS_FAILURE,
  BULK_SYNONYMS_REQUEST,
  BULK_SYNONYMS_SUCCESS,
  BULK_SYNONYMS_FAILURE,
  DELETE_SYNONYMS_REQUEST,
  DELETE_SYNONYMS_SUCCESS,
  DELETE_SYNONYMS_FAILURE,
  UPDATE_SYNONYM_REQUEST,
  UPDATE_SYNONYM_SUCCESS,
  UPDATE_SYNONYM_FAILURE,
  VERIFY_CONTENT_BEFORE_IMPORT_SYNONYMS_REQUEST,
  VERIFY_CONTENT_BEFORE_IMPORT_SYNONYMS_SUCCESS,
  VERIFY_CONTENT_BEFORE_IMPORT_SYNONYMS_FAILURE,
  IMPORT_FILE_SYNONYMS_REQUEST,
  IMPORT_FILE_SYNONYMS_SUCCESS,
  IMPORT_FILE_SYNONYMS_FAILURE,
  EXPORT_FILE_SYNONYMS_REQUEST,
  EXPORT_FILE_SYNONYMS_SUCCESS,
  EXPORT_FILE_SYNONYMS_FAILURE
} from 'stores/flow-type/synonym.type';
import { SynonymType } from 'src/constants/synonym';

const splitSynonym = (synonyms, type) => {
  if (type === SynonymType.MANUAL) {
    return !_.isEmpty(synonyms) ? synonyms.join(',') : '';
  }

  return synonyms || '';
};

const prepareSynonyms = (synonyms, type) =>
  _.map(synonyms, synonym => ({
    ...synonym,
    synonyms: splitSynonym(synonym.synonyms, type),
    biDirection: synonym.biDirection ? '<=>' : '=>'
  }));

const initialState: SynonymsState = {
  loading: false,
  success: false,
  updateSuccess: false,
  successFlag: 0, // Make sure data is updated when calling an API multiply at the same time
  error: null,
  synonyms: [],
  aiSynonyms: [],
  total: 0,
  saving: false,
  deleting: false,
  scheduleSyncIn: 0,
  scheduleSyncAfter: 0,
  waitSyncAt: 0,

  // Verify file content
  verifying: false,
  fileContentErrorCode: '',
  totalRecord: 0,

  // Import file content
  importing: false,
  totalImportedRecords: 0
};

const SynonymReducers = (state: SynonymsState = initialState, action: SynonymsAction) => {
  switch (action.type) {
    // Get synonyms
    case GET_SYNONYMS_REQUEST:
      return {
        ...state,
        success: false,
        loading: true,
        waitSyncAt: 0,
        scheduleSyncIn: 0,
        scheduleSyncAfter: 0
      };
    case GET_SYNONYMS_SUCCESS: {
      const { data, type } = action.payload;
      const { data: synonyms, total, waitSyncAt, scheduleSyncIn, scheduleSyncAfter } = data;

      return {
        ...state,
        loading: false,
        success: true,
        successFlag: 1 - state.successFlag,
        error: undefined,
        synonyms: prepareSynonyms(synonyms, type),
        aiSynonyms: type === SynonymType.AI ? prepareSynonyms(synonyms, type) : [],
        total,
        waitSyncAt,
        scheduleSyncIn,
        scheduleSyncAfter
      };
    }
    case GET_SYNONYMS_FAILURE:
      return {
        ...state,
        loading: false,
        success: false,
        error: action.payload
      };

    // Bulk synonyms
    case BULK_SYNONYMS_REQUEST:
      return {
        ...state,
        success: false,
        updateSuccess: false,
        saving: true,
        waitSyncAt: 0,
        scheduleSyncIn: 0,
        scheduleSyncAfter: 0
      };
    case BULK_SYNONYMS_SUCCESS: {
      const { data, scheduleSyncIn, scheduleSyncAfter, waitSyncAt } = action.payload;
      return {
        ...state,
        saving: false,
        success: true,
        updateSuccess: true,
        successFlag: 1 - state.successFlag,
        error: undefined,
        synonyms: prepareSynonyms(data.data),
        scheduleSyncIn,
        scheduleSyncAfter,
        waitSyncAt
      };
    }
    case BULK_SYNONYMS_FAILURE:
      return {
        ...state,
        saving: false,
        success: false,
        updateSuccess: false,
        error: action.payload
      };

    // Delete synonyms
    case DELETE_SYNONYMS_REQUEST:
      return {
        ...state,
        success: false,
        updateSuccess: false,
        deleting: true,
        waitSyncAt: 0,
        scheduleSyncIn: 0,
        scheduleSyncAfter: 0
      };
    case DELETE_SYNONYMS_SUCCESS: {
      const { data } = action.payload;
      if (!data) {
        return state;
      }
      const { scheduleSyncIn, scheduleSyncAfter, waitSyncAt } = data;
      return {
        ...state,
        deleting: false,
        success: true,
        updateSuccess: true,
        successFlag: 1 - state.successFlag,
        error: undefined,
        scheduleSyncIn,
        scheduleSyncAfter,
        waitSyncAt
      };
    }
    case DELETE_SYNONYMS_FAILURE:
      return {
        ...state,
        deleting: false,
        success: false,
        updateSuccess: false,
        error: action.payload
      };

    // Update suggestion
    case UPDATE_SYNONYM_REQUEST:
      return {
        ...state,
        success: false,
        updateSuccess: false,
        saving: true,
        waitSyncAt: 0,
        scheduleSyncIn: 0,
        scheduleSyncAfter: 0
      };
    case UPDATE_SYNONYM_SUCCESS: {
      const { data: response, actionName } = action.payload;
      const { scheduleSyncIn, scheduleSyncAfter, waitSyncAt } = response;
      if (actionName === 'changeAIStatus') {
        let { synonyms } = state;
        const { _id: id, status } = response;
        if (id) {
          let index = _.findIndex(synonyms, { _id: id });
          synonyms[index].status = status;
        }
        return {
          ...state,
          synonyms: [...synonyms]
        };
      }
      if (actionName === 'changeStatus') {
        const synonyms = _.cloneDeep(state.synonyms);
        const { id, enabled } = response.data[0];
        if (id) {
          let index = _.findIndex(synonyms, { id });
          synonyms[index].enabled = enabled;
        }

        return {
          ...state,
          saving: false,
          success: true,
          updateSuccess: true,
          successFlag: 1 - state.successFlag,
          error: undefined,
          synonyms,
          scheduleSyncAfter,
          scheduleSyncIn,
          waitSyncAt
        };
      }

      return {
        ...state,
        saving: false,
        success: true,
        updateSuccess: true,
        successFlag: 1 - state.successFlag,
        error: undefined,
        synonyms: prepareSynonyms(response.data),
        scheduleSyncAfter,
        scheduleSyncIn,
        waitSyncAt
      };
    }
    case UPDATE_SYNONYM_FAILURE:
      return {
        ...state,
        saving: false,
        success: false,
        updateSuccess: false,
        error: action.payload
      };

    // Verify file content
    case VERIFY_CONTENT_BEFORE_IMPORT_SYNONYMS_REQUEST:
      return { ...state, success: false, verifying: true, fileContentErrorCode: '' };
    case VERIFY_CONTENT_BEFORE_IMPORT_SYNONYMS_SUCCESS: {
      const { data } = action.payload;
      return {
        ...state,
        success: true,
        successFlag: 1 - state.successFlag,
        error: undefined,
        verifying: false,
        totalRecord: data.total,
        fileContentErrorCode: ''
      };
    }
    case VERIFY_CONTENT_BEFORE_IMPORT_SYNONYMS_FAILURE:
      return {
        ...state,
        success: false,
        error: action.payload,
        verifying: false,
        fileContentErrorCode: action.payload.errorCode ? action.payload.errorCode : 'ORTHER'
      };

    // Import synonyms
    case IMPORT_FILE_SYNONYMS_REQUEST:
      return {
        ...state,
        success: false,
        updateSuccess: false,
        importing: true,
        waitSyncAt: 0,
        scheduleSyncAfter: 0,
        scheduleSyncIn: 0
      };
    case IMPORT_FILE_SYNONYMS_SUCCESS: {
      const { data: response } = action.payload;
      const totalImportedRecords = _.isArray(response.data) ? response.data.length : 0;
      const { scheduleSyncAfter, scheduleSyncIn, waitSyncAt } = response.data;
      return {
        ...state,
        importing: false,
        success: true,
        updateSuccess: true,
        successFlag: 1 - state.successFlag,
        error: undefined,
        totalImportedRecords,
        scheduleSyncAfter,
        scheduleSyncIn,
        waitSyncAt
      };
    }
    case IMPORT_FILE_SYNONYMS_FAILURE:
      return {
        ...state,
        importing: false,
        success: false,
        updateSuccess: false,
        error: action.payload
      };

    // Export synonyms
    case EXPORT_FILE_SYNONYMS_REQUEST:
      return { ...state, success: false, exporting: true };
    case EXPORT_FILE_SYNONYMS_SUCCESS: {
      return {
        ...state,
        exporting: false,
        success: true,
        successFlag: 1 - state.successFlag,
        error: undefined
      };
    }
    case EXPORT_FILE_SYNONYMS_FAILURE:
      return {
        ...state,
        exporting: false,
        success: false,
        error: action.payload
      };

    default:
      return state;
  }
};
export default SynonymReducers;
